import { Heading } from "@tokenterminal/core/ui/heading/Heading"
import Image from "next/image"
import { twMerge } from "tailwind-merge"
import { MiniAuthor } from "@/components/resources/components/Author"
import { formatDate } from "@/components/resources/components/ui/utils"
import type { Article } from "@/components/resources/data/posts.types"
import { getOGImageRatioWidth } from "@/constants/blog-image"
import { BlogImage } from "../BlogImage"
import { ProjectLogo } from "../BlogImage/ProjectLogo"
import type { HeroProps } from "./Hero"

interface ArticleHeroProps
  extends Omit<HeroProps, "cta" | "lead" | "children">,
    Pick<
      Article,
      "publishedAt" | "category" | "releaseNumber" | "projectMeta" | "author"
    > {
  categoryName?: string
  image?: string
  subtitle?: string
}

const getCategoryColor = (cat: ArticleHeroProps["category"]): string => {
  switch (cat) {
    case "weekly-fundamentals":
      return "text-fg-emerald"
    case "interview":
      return "text-fg-emerald"
    case "crypto-research":
      return "text-fg-orange"
    default:
      return "text-fg-emerald"
  }
}

export const ArticleHero = ({
  title,
  categoryName,
  image,
  publishedAt,
  category,
  releaseNumber,
  projectMeta,
  author,
  subtitle,
  ...rest
}: ArticleHeroProps) => {
  return (
    <div
      className={twMerge(
        "text-center pt-6 md:pt-8 mb-8 md:mb-12 lg:mb-16 flex flex-col gap-8 md:gap-10 justify-self-center items-center"
      )}
      {...rest}
    >
      <div className="flex flex-col gap-6 items-center">
        {categoryName && (
          <p
            className={twMerge(
              "text-sm font-medium",
              getCategoryColor(category)
            )}
          >
            {categoryName}
          </p>
        )}
        <div className="flex flex-col gap-2 items-center">
          <Heading
            level={1}
            className="text-3xl lg:text-5xl whitespace-pre-line text-balance"
          >
            {title}
          </Heading>
          {subtitle && (
            <p className="text-base lg:text-lg text-fg-secondary text-center max-w-3xl">
              {subtitle}
            </p>
          )}
        </div>
        <div className="flex flex-row flex-wrap justify-center gap-2 items-center">
          <MiniAuthor {...author} />
          <span className="text-fg-secondary">•</span>
          {publishedAt && (
            <time
              className="text-sm text-fg-secondary font-medium"
              dateTime={formatDate(new Date(publishedAt))}
            >
              {formatDate(new Date(publishedAt), true)}
            </time>
          )}
          {projectMeta ? (
            <>
              <span className="text-fg-secondary">•</span>
              {/* A plain anchor: the Explorer is another app behind the same domain */}
              <a
                href={`/explorer/projects/${projectMeta.project_id}`}
                className="flex items-center gap-1.5 text-sm font-medium text-fg-secondary hover:text-fg-default"
              >
                <ProjectLogo
                  logo={projectMeta.logo}
                  name={projectMeta.name}
                  size={20}
                  alt=""
                />
                {projectMeta.name}
              </a>
            </>
          ) : null}
        </div>
      </div>
      {image ? (
        <Image
          src={image}
          alt={title}
          width={1170}
          height={getOGImageRatioWidth(1170)}
          className="rounded-2xl"
          priority
        />
      ) : (
        <BlogImage
          image={image}
          category={category}
          releaseNumber={releaseNumber}
          projectMeta={projectMeta}
          className="rounded-2xl"
          alt="Article hero image"
          width={1170}
          height={getOGImageRatioWidth(1170)}
          size="xxl"
          priority
        />
      )}
    </div>
  )
}

ArticleHero.displayName = "Hero"
